import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase } from './supabase';
import { adminLogin, adminLogout, getCurrentAdmin, startInactivityWatch } from './auth-api';
import type { AdminUser } from './types';

interface AuthContextValue {
  user: AdminUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    (async () => {
      const current = await getCurrentAdmin();
      if (mounted) {
        setUser(current);
        setLoading(false);
      }
    })();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      (async () => {
        if (!session) {
          setUser(null);
          return;
        }
        const current = await getCurrentAdmin();
        if (mounted) setUser(current);
      })();
    });

    const stopWatch = startInactivityWatch(() => {
      adminLogout();
      setUser(null);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
      stopWatch();
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const u = await adminLogin(email, password);
    setUser(u);
  }, []);

  const logout = useCallback(() => {
    adminLogout();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
